import React, { useEffect, useState } from 'react';
import { Agency } from '../../core/_models';
import { useAxios } from '../../core/_axios';
import LoadingSpinner from '../../components/LoadingSpinner';

interface User {
    unique_identifier: string;
    email: string;
    name: string;
    is_agency_admin?: boolean;
    inserted_at: string;
}

export default function ManageAgencyUsers({
    agency,
}: {
    agency: Agency;
}) {
    const axios = useAxios();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [showAdminsOnly, setShowAdminsOnly] = useState(false);

    // Add user modal state
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [addUserEmail, setAddUserEmail] = useState('');
    const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);
    const [addUserLoading, setAddUserLoading] = useState(false);
    const [addUserError, setAddUserError] = useState<string | null>(null);

    // Edit name modal state
    const [showEditNameModal, setShowEditNameModal] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [editName, setEditName] = useState('');
    const [editNameLoading, setEditNameLoading] = useState(false);
    const [editNameError, setEditNameError] = useState<string | null>(null);

    const [actionLoading, setActionLoading] = useState<string | null>(null);

    useEffect(() => {
        loadUsers();
    }, [agency.unique_identifier]);

    const loadUsers = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(
                `/api/v1/app/authenticated/admin/agencies/${agency.unique_identifier}/users`
            );

            if (response.data.data?.users) {
                setUsers(response.data.data.users);
            } else {
                setError('Failed to load users');
            }
        } catch (err) {
            console.error('Error loading users:', err);
            setError('An error occurred while loading users');
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async (e: React.FormEvent) => {
        e.preventDefault();
        setAddUserLoading(true);
        setAddUserError(null);

        try {
            const response = await axios.post(
                `/api/v1/app/authenticated/admin/agencies/${agency.unique_identifier}/add-user`,
                {
                    email: addUserEmail.trim(),
                    send_welcome_email: sendWelcomeEmail
                }
            );

            if (response.data.data?.user) {
                setShowAddUserModal(false);
                setAddUserEmail('');
                setSendWelcomeEmail(false);
                loadUsers();
            } else if (response.data.error) {
                setAddUserError(response.data.error);
            }
        } catch (err: any) {
            console.error('Error adding user:', err);
            setAddUserError(err.response?.data?.error || 'An error occurred while adding user');
        } finally {
            setAddUserLoading(false);
        }
    };

    const handleEditName = (user: User) => {
        setEditingUser(user);
        setEditName(user.name || '');
        setEditNameError(null);
        setShowEditNameModal(true);
    };

    const handleSaveName = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingUser) return;

        setEditNameLoading(true);
        setEditNameError(null);

        try {
            const response = await axios.post(
                `/api/v1/app/authenticated/admin/users/${editingUser.unique_identifier}/update-name`,
                { name: editName.trim() }
            );

            if (response.data.data?.user) {
                setShowEditNameModal(false);
                setEditingUser(null);
                setEditName('');
                loadUsers();
            } else if (response.data.error) {
                setEditNameError(response.data.error);
            }
        } catch (err: any) {
            console.error('Error updating name:', err);
            setEditNameError(err.response?.data?.error || 'An error occurred while updating name');
        } finally {
            setEditNameLoading(false);
        }
    };

    const handleToggleAdmin = async (user: User) => {
        setActionLoading(user.unique_identifier);

        try {
            const response = await axios.post(
                `/api/v1/app/authenticated/admin/users/${user.unique_identifier}/update-agency-admin-status`,
                { is_agency_admin: !user.is_agency_admin }
            );

            if (response.data.data?.user) {
                loadUsers();
            }
        } catch (err) {
            console.error('Error updating user admin status:', err);
        } finally {
            setActionLoading(null);
        }
    };

    const handleRemoveUser = async (user: User) => {
        if (!confirm(`Are you sure you want to remove ${user.email} from this agency?`)) {
            return;
        }

        setActionLoading(user.unique_identifier);

        try {
            const response = await axios.post(
                `/api/v1/app/authenticated/admin/users/${user.unique_identifier}/remove-from-agency`
            );

            if (response.data.data?.user) {
                loadUsers();
            }
        } catch (err) {
            console.error('Error removing user:', err);
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                <span className="block sm:inline">{error}</span>
            </div>
        );
    }

    const filteredUsers = showAdminsOnly
        ? users.filter(user => user.is_agency_admin)
        : users;

    return (
        <div className="grid grid-cols-1 gap-5">
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">Agency Users</h3>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setShowAddUserModal(true)}
                            className="btn btn-sm btn-primary"
                        >
                            Add User
                        </button>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showAdminsOnly}
                                onChange={(e) => setShowAdminsOnly(e.target.checked)}
                                className="form-checkbox h-4 w-4 text-blue-600"
                            />
                            <span className="text-sm text-gray-700">Admins only</span>
                        </label>
                        <div className="text-sm text-gray-600">
                            {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {filteredUsers.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            {showAdminsOnly
                                ? 'No admin users found for this agency'
                                : 'No users found for this agency'
                            }
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-auto">
                                <thead>
                                    <tr>
                                        <th className="min-w-[200px]">Name</th>
                                        <th className="min-w-[250px]">Email</th>
                                        <th className="min-w-[120px]">Role</th>
                                        <th className="min-w-[150px]">Joined</th>
                                        <th className="min-w-[180px]">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map((user) => (
                                        <tr key={user.unique_identifier}>
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-gray-700 text-sm font-semibold">
                                                        {(user.name || user.email).charAt(0).toUpperCase()}
                                                    </div>
                                                    <button
                                                        onClick={() => handleEditName(user)}
                                                        className={`font-medium hover:underline text-left ${
                                                            user.name ? 'text-gray-900' : 'text-gray-400 italic'
                                                        }`}
                                                    >
                                                        {user.name || '(Name not set)'}
                                                    </button>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="text-gray-600">{user.email}</span>
                                            </td>
                                            <td>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    user.is_agency_admin
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {user.is_agency_admin ? 'Admin' : 'User'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="text-gray-600 text-sm">
                                                    {new Date(user.inserted_at).toLocaleDateString()}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleToggleAdmin(user)}
                                                        disabled={actionLoading === user.unique_identifier}
                                                        className={`btn btn-xs ${user.is_agency_admin ? 'btn-warning' : 'btn-success'}`}
                                                    >
                                                        {actionLoading === user.unique_identifier ? (
                                                            '...'
                                                        ) : user.is_agency_admin ? (
                                                            'Demote'
                                                        ) : (
                                                            'Promote'
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => handleRemoveUser(user)}
                                                        disabled={actionLoading === user.unique_identifier}
                                                        className="btn btn-xs btn-danger"
                                                    >
                                                        {actionLoading === user.unique_identifier ? '...' : 'Remove'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Add User Modal */}
            {showAddUserModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold mb-4">Add User to Agency</h3>
                            <form onSubmit={handleAddUser}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        value={addUserEmail}
                                        onChange={(e) => setAddUserEmail(e.target.value)}
                                        placeholder="user@example.com"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                        autoFocus
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        If the user doesn't exist, a new account will be created.
                                    </p>
                                </div>
                                <div className="mb-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={sendWelcomeEmail}
                                            onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                                            className="form-checkbox h-4 w-4 text-blue-600"
                                        />
                                        <span className="text-sm text-gray-700">Send welcome email</span>
                                    </label>
                                    <p className="mt-1 text-xs text-gray-500 ml-6">
                                        Sends an email with a login link to the user.
                                    </p>
                                </div>
                                {addUserError && (
                                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                                        {addUserError}
                                    </div>
                                )}
                                <div className="flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowAddUserModal(false);
                                            setAddUserEmail('');
                                            setSendWelcomeEmail(false);
                                            setAddUserError(null);
                                        }}
                                        className="btn btn-light"
                                        disabled={addUserLoading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={addUserLoading || !addUserEmail.trim()}
                                    >
                                        {addUserLoading ? 'Adding...' : 'Add User'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Name Modal */}
            {showEditNameModal && editingUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold mb-2">Edit User Name</h3>
                            <p className="text-sm text-gray-500 mb-4">{editingUser.email}</p>
                            <form onSubmit={handleSaveName}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Full Name
                                    </label>
                                    <input
                                        type="text"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        placeholder="Enter full name"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        autoFocus
                                    />
                                </div>
                                {editNameError && (
                                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                                        {editNameError}
                                    </div>
                                )}
                                <div className="flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowEditNameModal(false);
                                            setEditingUser(null);
                                            setEditName('');
                                            setEditNameError(null);
                                        }}
                                        className="btn btn-light"
                                        disabled={editNameLoading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={editNameLoading}
                                    >
                                        {editNameLoading ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
